import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
} from '@lms/ui';
import { Megaphone, Save, Loader2 } from 'lucide-react';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';
import { useLanguage } from '../lib/LanguageContext';
import { usePrograms } from '../hooks/usePrograms';
import { useCreateAnnouncement, useUpdateAnnouncement } from '../hooks/useAnnouncements';
import type {
  Announcement,
  AnnouncementCategory,
  AnnouncementPriority,
  AnnouncementTarget,
  CreateAnnouncementInput,
} from '@lms/shared/types';

interface AnnouncementFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  announcementToEdit?: Announcement | null;
}

export function AnnouncementFormModal({
  isOpen,
  onClose,
  announcementToEdit,
}: AnnouncementFormModalProps) {
  const { t } = useLanguage();
  const { data: programsData } = usePrograms({ limit: 100 });
  const programs = programsData?.items || [];

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<AnnouncementCategory>('GENERAL');
  const [priority, setPriority] = useState<AnnouncementPriority>('NORMAL');
  const [targetAudience, setTargetAudience] = useState<AnnouncementTarget>('ALL');
  const [programId, setProgramId] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [startsAt, setStartsAt] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');

  const createMutation = useCreateAnnouncement();
  const updateMutation = useUpdateAnnouncement();
  const isSaving = createMutation.isPending || updateMutation.isPending;

  const [prevSyncKey, setPrevSyncKey] = useState<string>('');

  const syncKey = `${isOpen ? 'open' : 'closed'}_${announcementToEdit?.id || 'new'}`;
  if (syncKey !== prevSyncKey) {
    setPrevSyncKey(syncKey);
    if (announcementToEdit) {
      setTitle(announcementToEdit.title || '');
      setContent(announcementToEdit.content || '');
      setCategory(announcementToEdit.category || 'GENERAL');
      setPriority(announcementToEdit.priority || 'NORMAL');
      setTargetAudience(announcementToEdit.targetAudience || 'ALL');
      setProgramId(announcementToEdit.programId || '');
      setIsActive(announcementToEdit.isActive ?? true);
      setStartsAt(
        announcementToEdit.startsAt
          ? new Date(announcementToEdit.startsAt).toISOString().split('T')[0]
          : ''
      );
      setExpiresAt(
        announcementToEdit.expiresAt
          ? new Date(announcementToEdit.expiresAt).toISOString().split('T')[0]
          : ''
      );
    } else {
      setTitle('');
      setContent('');
      setCategory('GENERAL');
      setPriority('NORMAL');
      setTargetAudience('ALL');
      setProgramId('');
      setIsActive(true);
      setStartsAt(new Date().toISOString().split('T')[0]);
      setExpiresAt('');
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;

    const payload: CreateAnnouncementInput = {
      title: title.trim(),
      content: content.trim(),
      category,
      priority,
      targetAudience,
      programId: programId || undefined,
      isActive,
      startsAt: startsAt ? new Date(startsAt).toISOString() : new Date().toISOString(),
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
    };

    if (announcementToEdit) {
      await updateMutation.mutateAsync({
        id: announcementToEdit.id,
        data: payload,
      });
    } else {
      await createMutation.mutateAsync(payload);
    }

    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-primary/10 text-primary">
              <Megaphone className="w-6 h-6" />
            </div>
            <div>
              <DialogTitle className="text-xl font-headline font-bold text-on-surface">
                <Bilingual
                  {...(announcementToEdit
                    ? copy.announcements.modalEditTitle
                    : copy.announcements.modalCreateTitle)}
                />
              </DialogTitle>
              <DialogDescription className="text-xs text-on-surface-variant font-body mt-0.5">
                <Bilingual {...copy.announcements.listSubtitle} />
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 pt-2">
          {/* Judul */}
          <div className="space-y-2">
            <label className="text-xs font-label font-bold text-on-surface uppercase tracking-wider">
              <Bilingual {...copy.announcements.labelTitle} /> *
            </label>
            <input
              required
              className="w-full bg-surface-container-low rounded-xl px-4 py-3 text-sm font-body text-on-surface focus:ring-2 focus:ring-primary/20 outline-none transition-all"
              placeholder={t(copy.announcements.placeholderTitle)}
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
          </div>

          {/* Kategori, Prioritas, Target */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Kategori */}
            <div className="space-y-2">
              <label className="text-xs font-label font-bold text-on-surface uppercase tracking-wider">
                <Bilingual {...copy.announcements.labelCategory} />
              </label>
              <select
                className="w-full bg-surface-container-low rounded-xl px-3 py-2.5 text-sm font-body text-on-surface focus:ring-2 focus:ring-primary/20 outline-none"
                value={category}
                onChange={e => setCategory(e.target.value as AnnouncementCategory)}
              >
                <option value="GENERAL">{t(copy.announcements.catGeneral)}</option>
                <option value="ACADEMIC">{t(copy.announcements.catAcademic)}</option>
                <option value="IMPORTANT">{t(copy.announcements.catImportant)}</option>
                <option value="MAINTENANCE">{t(copy.announcements.catMaintenance)}</option>
                <option value="EVENT">{t(copy.announcements.catEvent)}</option>
              </select>
            </div>

            {/* Prioritas */}
            <div className="space-y-2">
              <label className="text-xs font-label font-bold text-on-surface uppercase tracking-wider">
                <Bilingual {...copy.announcements.labelPriority} />
              </label>
              <select
                className="w-full bg-surface-container-low rounded-xl px-3 py-2.5 text-sm font-body text-on-surface focus:ring-2 focus:ring-primary/20 outline-none"
                value={priority}
                onChange={e => setPriority(e.target.value as AnnouncementPriority)}
              >
                <option value="NORMAL">{t(copy.announcements.prioNormal)}</option>
                <option value="HIGH">{t(copy.announcements.prioHigh)}</option>
                <option value="URGENT">{t(copy.announcements.prioUrgent)}</option>
              </select>
            </div>

            {/* Target Audiens */}
            <div className="space-y-2">
              <label className="text-xs font-label font-bold text-on-surface uppercase tracking-wider">
                <Bilingual {...copy.announcements.labelTarget} />
              </label>
              <select
                className="w-full bg-surface-container-low rounded-xl px-3 py-2.5 text-sm font-body text-on-surface focus:ring-2 focus:ring-primary/20 outline-none"
                value={targetAudience}
                onChange={e => setTargetAudience(e.target.value as AnnouncementTarget)}
              >
                <option value="ALL">{t(copy.announcements.targetAll)}</option>
                <option value="PARTICIPANT_ONLY">{t(copy.announcements.targetParticipant)}</option>
                <option value="TEACHER_ONLY">{t(copy.announcements.targetTeacher)}</option>
                <option value="ADMIN_ONLY">{t(copy.announcements.targetAdmin)}</option>
              </select>
            </div>
          </div>

          {/* Program Relation */}
          <div className="space-y-2">
            <label className="text-xs font-label font-bold text-on-surface uppercase tracking-wider">
              <Bilingual {...copy.announcements.labelProgram} />
            </label>
            <select
              className="w-full bg-surface-container-low rounded-xl px-4 py-2.5 text-sm font-body text-on-surface focus:ring-2 focus:ring-primary/20 outline-none"
              value={programId}
              onChange={e => setProgramId(e.target.value)}
            >
              <option value="">{t(copy.announcements.selectProgramPlaceholder)}</option>
              {programs.map(p => (
                <option key={p.id} value={p.id}>
                  [{p.code}] {p.name}
                </option>
              ))}
            </select>
          </div>

          {/* Isi Pengumuman */}
          <div className="space-y-2">
            <label className="text-xs font-label font-bold text-on-surface uppercase tracking-wider">
              <Bilingual {...copy.announcements.labelContent} /> *
            </label>
            <textarea
              required
              rows={5}
              className="w-full bg-surface-container-low rounded-xl p-4 text-sm font-body text-on-surface focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-y"
              placeholder={t(copy.announcements.placeholderContent)}
              value={content}
              onChange={e => setContent(e.target.value)}
            />
          </div>

          {/* Tanggal & Status */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
            <div className="space-y-2">
              <label className="text-xs font-label font-bold text-on-surface uppercase tracking-wider">
                <Bilingual {...copy.announcements.labelStartsAt} />
              </label>
              <input
                type="date"
                className="w-full bg-surface-container-low rounded-xl px-3 py-2 text-sm font-body text-on-surface focus:ring-2 focus:ring-primary/20 outline-none"
                value={startsAt}
                onChange={e => setStartsAt(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-label font-bold text-on-surface uppercase tracking-wider">
                <Bilingual {...copy.announcements.labelExpiresAt} />
              </label>
              <input
                type="date"
                className="w-full bg-surface-container-low rounded-xl px-3 py-2 text-sm font-body text-on-surface focus:ring-2 focus:ring-primary/20 outline-none"
                value={expiresAt}
                onChange={e => setExpiresAt(e.target.value)}
              />
            </div>

            <div className="flex flex-col justify-center space-y-2">
              <label className="text-xs font-label font-bold text-on-surface uppercase tracking-wider">
                <Bilingual {...copy.announcements.labelStatusActive} />
              </label>
              <label className="inline-flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={isActive}
                  onChange={e => setIsActive(e.target.checked)}
                />
                <div className="w-11 h-6 bg-surface-container-high peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary relative" />
                <span className="text-xs font-body font-bold text-on-surface">
                  {isActive
                    ? t(copy.announcements.statusActive)
                    : t(copy.announcements.statusInactive)}
                </span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-6 border-t border-on-surface/5">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl font-label font-bold text-xs uppercase tracking-wider"
            >
              <Bilingual {...copy.announcements.btnCancel} />
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-primary text-on-primary px-8 py-2.5 rounded-xl font-label font-bold text-xs uppercase tracking-wider flex items-center gap-2 shadow-level1 hover:bg-primary-container"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <Bilingual {...copy.announcements.btnSave} />
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
